import React, { useState } from 'react'
import Accordion from 'react-bootstrap/Accordion'
import { getNotificationHost } from '../../../../modules/apiHost'
import ApiStoreSpecExports from './ApiStoreSpecExports'

type FieldRow = {
  field: string
  type?: string
  notes?: string
  required?: boolean
}

function FieldTable({ rows }: { rows: FieldRow[] }) {
  return (
    <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-w-full tw-text-sm tw-shadow-sm tw-max-h-56 tw-overflow-y-auto">
      <table className="tw-table-auto tw-border-collapse tw-w-full">
        <thead className="tw-sticky tw-top-0 tw-z-[1] tw-border-b tw-font-medium tw-text-slate-500 tw-bg-slate-50 tw-text-left">
          <tr>
            <th className="tw-px-2 tw-py-1.5">Field</th>
            <th className="tw-px-2 tw-py-1.5">Type</th>
            <th className="tw-px-2 tw-py-1.5">Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.field}
              className={index !== 0 ? 'tw-border-t tw-border-slate-200' : ''}
            >
              <td className="tw-px-2 tw-py-1.5 tw-font-mono tw-text-xs tw-text-slate-800 tw-align-top tw-whitespace-nowrap">
                {row.required ? `${row.field} *` : row.field}
              </td>
              <td className="tw-px-2 tw-py-1.5 tw-text-slate-600 tw-align-top tw-whitespace-nowrap">
                {row.type || '—'}
              </td>
              <td className="tw-px-2 tw-py-1.5 tw-text-slate-600 tw-align-top">{row.notes || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const REQUIRED_ORDER_FIELDS: FieldRow[] = [
  { field: 'order_number', type: 'number | string', notes: 'External order number. Used for Solr id when order_id is omitted.', required: true },
  { field: 'total_price', type: 'number', notes: 'Order total (2 decimal places).', required: true },
  { field: 'currency', type: 'string', notes: 'ISO 4217 code, e.g. USD.', required: true },
  { field: 'created_at', type: 'string', notes: 'ISO-8601 UTC. Used for dashboard date filters when processed_at is omitted.', required: true },
  { field: 'line_items', type: 'array', notes: 'At least one line.', required: true },
]

const RECOMMENDED_ORDER_FIELDS: FieldRow[] = [
  { field: 'order_id', type: 'string', notes: 'Stable external id when available.' },
  { field: 'financial_status', type: 'string', notes: 'pending | authorized | paid | partially_paid | partially_refunded | refunded | voided | cancelled' },
  { field: 'fulfillment_status', type: 'string', notes: 'unfulfilled | partial | fulfilled | restocked' },
  { field: 'processed_at', type: 'string', notes: 'ISO-8601 UTC. Dashboard "Placed at" filter; defaults to created_at when omitted.' },
  { field: 'updated_at', type: 'string', notes: 'ISO-8601 UTC.' },
  { field: 'subtotal_price', type: 'number', notes: 'Order subtotal.' },
  { field: 'total_tax', type: 'number', notes: 'Total tax.' },
  { field: 'total_discounts', type: 'number', notes: 'Total discounts.' },
  { field: 'total_shipping_price', type: 'number', notes: 'Total shipping.' },
  { field: 'tags', type: 'string[]', notes: 'Order tags.' },
  { field: 'coupon_codes', type: 'string[]', notes: 'Applied coupons.' },
]

const LABELED_FACET_FIELDS: FieldRow[] = [
  { field: 'custom_fields', type: 'string[]', notes: 'Use "Label:Value" strings, e.g. Color:Blue.' },
  { field: 'customer_form_v1_fields', type: 'string[]', notes: '"Label:Value" strings.' },
  { field: 'shipping_form_fields', type: 'string[]', notes: '"Label:Value" strings.' },
  { field: 'customer_custom_attributes', type: 'string[]', notes: '"Label:Value" strings.' },
]

const CUSTOMER_INFO_FIELDS: FieldRow[] = [
  { field: 'customer_id', type: 'string', notes: 'Required on customer object.', required: true },
  { field: 'customer_email', type: 'string', notes: '' },
  { field: 'customer_first_name', type: 'string', notes: '' },
  { field: 'customer_last_name', type: 'string', notes: '' },
  { field: 'customer_phone', type: 'string', notes: '' },
  { field: 'customer_group_name', type: 'string', notes: '' },
  { field: 'customer_message', type: 'string', notes: '' },
  { field: 'customer_note', type: 'string', notes: '' },
]

const BILLING_INFO_FIELDS: FieldRow[] = [
  { field: 'billing_country_code', type: 'string', notes: 'Required. ISO 3166-1 alpha-2, e.g. US.', required: true },
  { field: 'billing_address1', type: 'string', notes: '' },
  { field: 'billing_city', type: 'string', notes: '' },
  { field: 'billing_zip', type: 'string', notes: '' },
]

const SHIPPING_INFO_FIELDS: FieldRow[] = [
  { field: 'shipping_id', type: 'string', notes: 'Required. Unique per shipment row.', required: true },
  { field: 'shipping_country_code', type: 'string', notes: 'Required. ISO 3166-1 alpha-2.', required: true },
  { field: 'tracking_number', type: 'string', notes: '' },
  { field: 'tracking_company', type: 'string', notes: '' },
  { field: 'shipped_at', type: 'string', notes: '' },
  { field: 'fulfilled_at', type: 'string', notes: '' },
  { field: 'shipping_address_1', type: 'string', notes: '' },
  { field: 'shipping_city', type: 'string', notes: '' },
  { field: 'shipping_zip', type: 'string', notes: '' },
]

const LINE_ITEM_FIELDS: FieldRow[] = [
  { field: 'product_id', type: 'string', notes: 'Product identifier.' },
  { field: 'quantity', type: 'number', notes: 'Line quantity.' },
  { field: 'total_price', type: 'number', notes: 'Line total.' },
  { field: 'total_tax', type: 'number', notes: 'Line tax.' },
  { field: 'sku', type: 'string', notes: 'SKU / variant identifiers.' },
  { field: 'product_sku', type: 'string', notes: '' },
  { field: 'variant_id', type: 'string', notes: '' },
  { field: 'variant_title', type: 'string', notes: '' },
  { field: 'image', type: 'string', notes: 'Product image URL.' },
  { field: 'product_path', type: 'string', notes: 'Product link.' },
  { field: 'options', type: 'string[]', notes: 'Selected options.' },
  { field: 'custom_fields', type: 'string[]', notes: '"Label:Value" strings.' },
]

const EXAMPLE_REQUEST = `{
  "order_number": "1001",
  "order_id": "ext-1001",
  "total_price": 99.99,
  "currency": "USD",
  "financial_status": "paid",
  "fulfillment_status": "unfulfilled",
  "created_at": "2026-06-03T12:00:00Z",
  "total_tip": 5.0,
  "line_items": [
    {
      "product_id": "123",
      "sku": "SKU-123",
      "quantity": 1,
      "total_price": 99.99,
      "total_discount": 2.5
    }
  ],
  "customer": {
    "customer_id": "42",
    "customer_email": "buyer@example.com",
    "customer_first_name": "Jane",
    "customer_last_name": "Doe"
  },
  "billing": {
    "billing_country_code": "US",
    "billing_address1": "123 Main St",
    "billing_city": "Austin",
    "billing_zip": "78701"
  },
  "shipping": [
    {
      "shipping_id": "ship-1",
      "shipping_country_code": "US",
      "shipping_address_1": "123 Main St",
      "shipping_address_2": "Apt 4",
      "shipping_city": "Austin",
      "shipping_zip": "78701"
    }
  ],
  "marketing": {
    "utm_source": "google",
    "utm_campaign": "spring_sale"
  },
  "transactions": [
    {
      "id": "txn-1",
      "amount": 99.99,
      "currency": "USD",
      "method": "credit_card",
      "gateway_fee": 2.99,
      "status": "success"
    }
  ]
}`

const ACCORDION_SECTIONS = [
  {
    eventKey: 'getting-started',
    title: 'Endpoint & authentication',
    headerClassName:
      '!tw-bg-sky-50 hover:!tw-bg-sky-100 [&:not(.collapsed)]:!tw-bg-sky-100 !tw-text-sky-900 !tw-border-l-4 !tw-border-l-sky-500',
    bodyClassName: 'tw-pt-2 tw-space-y-3 tw-bg-white',
  },
  {
    eventKey: 'required-fields',
    title: 'Required order fields',
    headerClassName:
      '!tw-bg-amber-50 hover:!tw-bg-amber-100 [&:not(.collapsed)]:!tw-bg-amber-100 !tw-text-amber-950 !tw-border-l-4 !tw-border-l-amber-500',
    bodyClassName: 'tw-pt-2 tw-bg-white',
  },
  {
    eventKey: 'recommended-fields',
    title: 'Recommended order fields',
    headerClassName:
      '!tw-bg-emerald-50 hover:!tw-bg-emerald-100 [&:not(.collapsed)]:!tw-bg-emerald-100 !tw-text-emerald-950 !tw-border-l-4 !tw-border-l-emerald-500',
    bodyClassName: 'tw-pt-2 tw-space-y-4 tw-bg-white',
  },
  {
    eventKey: 'nested-objects',
    title: 'Nested objects',
    headerClassName:
      '!tw-bg-violet-50 hover:!tw-bg-violet-100 [&:not(.collapsed)]:!tw-bg-violet-100 !tw-text-violet-950 !tw-border-l-4 !tw-border-l-violet-500',
    bodyClassName: 'tw-pt-2 tw-space-y-4 tw-bg-white',
  },
  {
    eventKey: 'example',
    title: 'Example request & responses',
    headerClassName:
      '!tw-bg-indigo-50 hover:!tw-bg-indigo-100 [&:not(.collapsed)]:!tw-bg-indigo-100 !tw-text-indigo-950 !tw-border-l-4 !tw-border-l-indigo-500',
    bodyClassName: 'tw-pt-2 tw-space-y-4 tw-bg-white',
  },
] as const

export default function ApiStoreImportDocsAccordion() {
  const orderEndpoint = `${getNotificationHost(window.location.hostname)}/api/v1/orders`
  const batchEndpoint = `${getNotificationHost(window.location.hostname)}/api/v1/orders/batch`
  const [exampleJson, setExampleJson] = useState(EXAMPLE_REQUEST)

  return (
    <div className="tw-mt-4 tw-mb-6">
      <div className="tw-rounded-lg tw-border tw-border-sky-200 tw-bg-sky-50/60 tw-px-3 tw-py-2 tw-mb-3">
        <p className="tw-text-sm tw-text-sky-950 tw-mb-0">
          Prefer <code className="tw-text-xs">POST /api/v1/orders</code>. Successful writes return{' '}
          <code className="tw-text-xs">202</code> with <code className="tw-text-xs">job_id</code> after enqueue.
          Re-POSTing the same <code className="tw-text-xs">order_id</code> upserts. No DELETE — cancel via status fields.
          Legacy <code className="tw-text-xs">POST /api/order</code> remains available but is deprecated.
        </p>
      </div>
      <ApiStoreSpecExports exampleJson={exampleJson} onExampleLoaded={setExampleJson} />
      <Accordion
        alwaysOpen
        className="tw-rounded-xl tw-border tw-border-slate-200 tw-overflow-hidden tw-shadow-sm [&_.accordion-button]:!tw-font-medium [&_.accordion-button]:!tw-py-3 [&_.accordion-button]:focus:!tw-shadow-none [&_.accordion-button]:focus:!tw-border-transparent"
      >
        <Accordion.Item eventKey="getting-started" className="tw-border-slate-200">
          <Accordion.Header className={ACCORDION_SECTIONS[0].headerClassName}>
            {ACCORDION_SECTIONS[0].title}
          </Accordion.Header>
          <Accordion.Body className={ACCORDION_SECTIONS[0].bodyClassName}>
            <div>
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 tw-mb-1">
                Endpoints
              </p>
              <code className="tw-block tw-text-xs tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded tw-px-2 tw-py-1.5 tw-break-all tw-mb-1">
                POST {orderEndpoint}
              </code>
              <code className="tw-block tw-text-xs tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded tw-px-2 tw-py-1.5 tw-break-all tw-mb-1">
                POST {batchEndpoint}
              </code>
              <code className="tw-block tw-text-xs tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded tw-px-2 tw-py-1.5 tw-break-all tw-mb-1">
                GET {orderEndpoint}/:order_id
              </code>
              <code className="tw-block tw-text-xs tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded tw-px-2 tw-py-1.5 tw-break-all">
                PATCH {orderEndpoint}/:order_id
              </code>
            </div>
            <div>
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 tw-mb-1">
                Authentication
              </p>
              <p className="tw-text-sm tw-text-slate-600 tw-mb-2">
                Send header <code className="tw-text-xs">x-api-key</code> set to the API key generated above.
              </p>
              <FieldTable
                rows={[
                  { field: 'x-api-key', type: 'header', notes: 'Required. API key from this integration.', required: true },
                ]}
              />
            </div>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="required-fields" className="tw-border-slate-200">
          <Accordion.Header className={ACCORDION_SECTIONS[1].headerClassName}>
            {ACCORDION_SECTIONS[1].title}
          </Accordion.Header>
          <Accordion.Body className={ACCORDION_SECTIONS[1].bodyClassName}>
            <FieldTable rows={REQUIRED_ORDER_FIELDS} />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="recommended-fields" className="tw-border-slate-200">
          <Accordion.Header className={ACCORDION_SECTIONS[2].headerClassName}>
            {ACCORDION_SECTIONS[2].title}
          </Accordion.Header>
          <Accordion.Body className={ACCORDION_SECTIONS[2].bodyClassName}>
            <FieldTable rows={RECOMMENDED_ORDER_FIELDS} />
            <div>
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 tw-mb-1">
                Labeled facet fields
              </p>
              <FieldTable rows={LABELED_FACET_FIELDS} />
            </div>
            <p className="tw-text-xs tw-text-slate-500 tw-mb-0">
              Optional rollup arrays include <code className="tw-text-xs">options</code>,{' '}
              <code className="tw-text-xs">category_ids</code>, <code className="tw-text-xs">brand_names</code>,{' '}
              <code className="tw-text-xs">skus</code>, <code className="tw-text-xs">product_ids</code>, and related
              product/shipping identifiers.
            </p>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="nested-objects" className="tw-border-slate-200">
          <Accordion.Header className={ACCORDION_SECTIONS[3].headerClassName}>
            {ACCORDION_SECTIONS[3].title}
          </Accordion.Header>
          <Accordion.Body className={ACCORDION_SECTIONS[3].bodyClassName}>
            <div>
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 tw-mb-1">
                customer
              </p>
              <FieldTable rows={CUSTOMER_INFO_FIELDS} />
            </div>
            <div>
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 tw-mb-1">
                billing
              </p>
              <FieldTable rows={BILLING_INFO_FIELDS} />
            </div>
            <div>
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 tw-mb-1">
                shipping[]
              </p>
              <FieldTable rows={SHIPPING_INFO_FIELDS} />
            </div>
            <div>
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 tw-mb-1">
                line_items[]
              </p>
              <FieldTable rows={LINE_ITEM_FIELDS} />
            </div>
            <p className="tw-text-xs tw-text-slate-500 tw-mb-0">
              Legacy array shapes (<code className="tw-text-xs">customer_info[]</code>,{' '}
              <code className="tw-text-xs">billing_info[]</code>, <code className="tw-text-xs">shipping_info[]</code>) are
              still accepted. Optional arrays also include <code className="tw-text-xs">transactions[]</code> and{' '}
              <code className="tw-text-xs">product_info[]</code>.
            </p>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="example" className="tw-border-slate-200">
          <Accordion.Header className={ACCORDION_SECTIONS[4].headerClassName}>
            {ACCORDION_SECTIONS[4].title}
          </Accordion.Header>
          <Accordion.Body className={ACCORDION_SECTIONS[4].bodyClassName}>
            <div>
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 tw-mb-1">
                Example body
              </p>
              <pre className="tw-text-xs tw-bg-slate-50 tw-border tw-border-slate-200 tw-rounded tw-p-3 tw-overflow-x-auto tw-max-h-64 tw-overflow-y-auto tw-mb-0">
                {exampleJson}
              </pre>
            </div>
            <div>
              <p className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-slate-500 tw-mb-1">
                Responses
              </p>
              <FieldTable
                rows={[
                  { field: '202', type: 'success', notes: 'Order accepted. Body includes order_id, job_id, solr_id.' },
                  { field: '400', type: 'error', notes: 'Validation error in request body.' },
                  { field: '401', type: 'error', notes: 'Invalid or missing API key / no API store account.' },
                  { field: '429', type: 'error', notes: 'Rate limited (per API key).' },
                  { field: '502', type: 'error', notes: 'Accepted validation but failed to enqueue.' },
                ]}
              />
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  )
}
